import { useEffect } from 'react'

import { Box, Button, Heading, HStack, Stack } from '@chakra-ui/react'
import {
  Field,
  SectionTypeDef,
  RepeatableSectionTypeDef,
} from '@micin-jp/chicken-schema'

import { PrintFooter } from './PrintFooter'
import { PrintPatientInfo } from './PrintPatientInfo'
import { PrintRepeatableSection } from './PrintRepeatableSection'
import { PrintSection } from './PrintSection'
import { PatientInfo, WorksheetDetail, WorksheetLog } from '../../types'
import { getFlattenFieldMap } from '../../utils/getFlattenFields'

import './worksheetPrint.css'

type Props = {
  worksheet: WorksheetDetail
  patient?: PatientInfo
}

type SectionField = Field & { typeDef: SectionTypeDef }
type RepeatableSectionField = Field & { typeDef: RepeatableSectionTypeDef }

/**
 * 指示確定ログを取得
 */
const findSendLog = (logs: WorksheetLog[]): WorksheetLog | undefined => {
  return logs.find(log => log.action === 'deSend')
}

/**
 * 受領確定ログを取得
 */
const findAcceptLog = (logs: WorksheetLog[]): WorksheetLog | undefined => {
  return logs.find(log => log.action === 'deAccept')
}

/**
 * Sectionフィールドかどうかを判定
 */
const isSectionField = (field: Field): field is SectionField => {
  return field.typeDef.type === 'Section'
}

/**
 * RepeatableSectionフィールドかどうかを判定
 */
const isRepeatableSectionField = (
  field: Field,
): field is RepeatableSectionField => {
  return field.typeDef.type === 'RepeatableSection'
}

export const WorksheetPrintView: React.FC<Props> = ({ worksheet, patient }) => {
  const fieldMap = getFlattenFieldMap(worksheet.schema.fields)
  const fieldValues = worksheet.latestWorksheetLog.fieldValues
  const sendLog = findSendLog(worksheet.worksheetLogs)
  const acceptLog = findAcceptLog(worksheet.worksheetLogs)

  // ページタイトルをワークシート名に設定（PDF保存時のファイル名になる）
  useEffect(() => {
    const originalTitle = document.title
    document.title = worksheet.name
    return () => {
      document.title = originalTitle
    }
  }, [worksheet.name])

  const handlePrint = () => {
    window.print()
  }

  return (
    <Box>
      {/* 印刷ボタン（画面表示時のみ、印刷時は非表示） */}
      <Box
        className="no-print"
        position="fixed"
        top="4"
        right="4"
        zIndex="1000"
        bg="white"
        p="2"
        borderRadius="md"
        shadow="md"
      >
        <HStack>
          <Button colorScheme="blue" onClick={handlePrint}>
            印刷する
          </Button>
        </HStack>
      </Box>

      <Box className="worksheet-print-view">
        {/* H1: ワークシート表示名 */}
        <Heading
          as="h1"
          textAlign="center"
          fontSize="2xl"
          fontWeight="bold"
          mb="8"
          className="print-title"
        >
          {worksheet.name}
        </Heading>

        {/* 患者情報 */}
        {patient && <PrintPatientInfo patient={patient} />}

        {/* セクションごとのフィールド表示 */}
        <Stack spacing="8">
          {worksheet.schema.fields.map(field => {
            if (isSectionField(field)) {
              return (
                <PrintSection
                  key={field.fid}
                  section={field}
                  fieldValues={fieldValues}
                  fieldMap={fieldMap}
                />
              )
            }
            if (isRepeatableSectionField(field)) {
              return (
                <PrintRepeatableSection
                  key={field.fid}
                  repeatableSection={field}
                  fieldValues={fieldValues}
                  fieldMap={fieldMap}
                />
              )
            }
            return null
          })}
        </Stack>

        {/* 指示確定・受領確定情報 */}
        {(sendLog || acceptLog) && (
          <PrintFooter sendLog={sendLog} acceptLog={acceptLog} />
        )}
      </Box>
    </Box>
  )
}
